/**
 * CheckInChat
 *
 * Guided check-in conversation for a single focus area.
 *
 * Renders: focus area context + progress through the 3 questions, message
 * history, the streaming assistant reply, and the composer. Hands control back
 * to the page via onAutoFinalized once the user has read the closing
 * reflection.
 *
 * Progress is derived from the user's answer count rather than tracked
 * separately, because the backend auto-finalizes on the 3rd user turn — a
 * separate counter could drift out of step with the conversation itself.
 */

import { useState, useRef, useEffect } from "react";
import { useIsCoarsePointer } from "@/hooks/useIsCoarsePointer";
import { shouldSendOnEnter } from "../../lib/composerKeys";
import { Loader2, Send, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useStreamCheckInMessage } from "@/hooks/useFocusAreaCheckIn";
import { CheckInProgress, TOTAL_QUESTIONS } from "./CheckInProgress";
import { TypingDots } from "./TypingDots";
import type { FocusArea } from "@/types/focusArea";
import type { FocusAreaCheckIn } from "@/types/focusAreaCheckIn";
import type { ProgressSignal } from "@/types/focusAreaCheckIn";

interface CheckInChatProps {
  focusArea: FocusArea;
  checkIn: FocusAreaCheckIn;
  onAutoFinalized: (
    proposedSummary: string,
    proposedProgressSignal: ProgressSignal
  ) => void;
  onFinishEarly: () => void;
}

export function CheckInChat({
  focusArea,
  checkIn,
  onAutoFinalized,
  onFinishEarly,
}: CheckInChatProps) {
  const [inputValue, setInputValue] = useState("");
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(
    null
  );
  // When the backend auto-finalizes on turn 3, stash the proposed summary/
  // signal and show a "Continue to summary" button so the user has time to
  // read the LLM's closing message before being whisked to the confirm view.
  const [pendingFinalize, setPendingFinalize] = useState<{
    summary: string;
    signal: ProgressSignal;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isCoarsePointer = useIsCoarsePointer();

  const { start, isStreaming, streamingContent, reset } =
    useStreamCheckInMessage();

  // Answers given so far. Includes the optimistic pending message so the bar
  // advances the moment the user sends, not a round-trip later.
  const answeredCount =
    checkIn.messages.filter((msg) => msg.role === "user").length +
    (pendingUserMessage ? 1 : 0);

  // Scroll to bottom when messages or streaming content changes
  useEffect(() => {
    if (
      messagesEndRef.current &&
      typeof messagesEndRef.current.scrollIntoView === "function"
    ) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [checkIn.messages, streamingContent, pendingUserMessage]);

  // Clear pending user message once the check-in refreshes with the new message
  useEffect(() => {
    if (pendingUserMessage && !isStreaming) {
      setPendingUserMessage(null);
      reset();
    }
  }, [checkIn.messages.length, isStreaming, pendingUserMessage, reset]);

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isStreaming) return;

    setInputValue("");
    setPendingUserMessage(trimmed);

    await start({
      checkInId: checkIn.id,
      message: trimmed,
      callbacks: {
        onContent: () => {
          // content is accumulated in streamingContent via the hook
        },
        onDone: (data) => {
          if (
            data.auto_finalized &&
            data.proposed_summary &&
            data.proposed_progress_signal
          ) {
            // Don't transition immediately — let the user read the closing
            // reflection first. They'll click "Continue to summary" when
            // ready.
            setPendingFinalize({
              summary: data.proposed_summary,
              signal: data.proposed_progress_signal as ProgressSignal,
            });
          }
        },
        onError: (error) => {
          console.error("Check-in streaming error:", error);
        },
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (shouldSendOnEnter(e)) {
      e.preventDefault();
      void handleSend();
    }
  };

  // Wrapping up early only makes sense once there is something to summarise,
  // and it is meaningless on the last question — the check-in finalizes itself.
  const canWrapUpEarly =
    !pendingFinalize &&
    answeredCount > 0 &&
    answeredCount < TOTAL_QUESTIONS &&
    !isStreaming;

  return (
    <div className="flex h-full flex-col">
      {/* Header: what we're discussing, and how far along */}
      <div className="shrink-0 border-b bg-background px-4 py-3">
        <div className="mx-auto w-full max-w-3xl">
          <h2 className="text-base font-semibold leading-tight text-foreground">
            {focusArea.name}
          </h2>
          {focusArea.current_practice && (
            <p className="mt-1 text-sm text-muted-foreground">
              <span className="text-muted-foreground/70">Practicing: </span>
              {focusArea.current_practice.text}
            </p>
          )}
          <CheckInProgress
            answered={answeredCount}
            isComplete={pendingFinalize !== null}
            className="mt-3"
          />
        </div>
      </div>

      {/* Messages */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <div className="mx-auto w-full max-w-3xl space-y-4">
          {checkIn.messages.map((msg) => (
            <MessageRow key={msg.id} role={msg.role} content={msg.content} />
          ))}

          {/* Pending user message while streaming */}
          {pendingUserMessage && (
            <MessageRow role="user" content={pendingUserMessage} />
          )}

          {/* Streaming assistant content */}
          {(isStreaming || streamingContent) && (
            <MessageRow
              role="assistant"
              content={streamingContent}
              fallback={<TypingDots />}
            />
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Composer, or the hand-off once the conversation is done */}
      <div className="shrink-0 border-t bg-background px-4 py-3">
        <div className="mx-auto w-full max-w-3xl">
          {pendingFinalize ? (
            <div className="space-y-2">
              <p className="text-center text-sm text-muted-foreground">
                That's all three questions. Next you'll review the summary
                before it's saved.
              </p>
              <Button
                onClick={() =>
                  onAutoFinalized(
                    pendingFinalize.summary,
                    pendingFinalize.signal
                  )
                }
                className="w-full"
                data-testid="continue-to-summary-button"
              >
                Review summary
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-end gap-2">
                <Textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your response..."
                  className="min-h-[60px] flex-1 resize-none"
                  disabled={isStreaming}
                  data-testid="check-in-input"
                />
                <Button
                  onClick={() => void handleSend()}
                  disabled={isStreaming || inputValue.trim().length === 0}
                  size="icon"
                  aria-label="Send response"
                  data-testid="send-button"
                >
                  {isStreaming ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" aria-hidden="true" />
                  )}
                </Button>
              </div>

              <div className="mt-2 flex min-h-[1.75rem] items-center justify-between gap-3">
                {/* Keyboard hint only on keyboard devices. Gating on width was
                  wrong in both directions: a small laptop window hid it, and a
                  tablet in landscape showed Shift+Enter advice to a device with
                  no Shift key. */}
                <p
                  className={`text-xs text-muted-foreground ${
                    isCoarsePointer ? "hidden" : "hidden sm:block"
                  }`}
                >
                  <kbd className="rounded border border-border px-1 py-0.5 font-sans text-[10px]">
                    Enter
                  </kbd>{" "}
                  to send,{" "}
                  <kbd className="rounded border border-border px-1 py-0.5 font-sans text-[10px]">
                    Shift + Enter
                  </kbd>{" "}
                  for a new line
                </p>

                {/*
                Demoted deliberately. As a full-width outlined button directly
                under the composer this read as "submit my answer" and competed
                with Send, when it actually ends the conversation early and
                jumps to the summary. It is now a quiet text action, and only
                appears once there is something worth summarising.
              */}
                {canWrapUpEarly && (
                  <button
                    type="button"
                    onClick={onFinishEarly}
                    className="ml-auto shrink-0 rounded text-xs font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="finish-early-button"
                  >
                    Skip remaining questions
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * One message. The assistant gets an icon so the two voices are separable at a
 * glance without relying on alignment alone.
 */
function MessageRow({
  role,
  content,
  fallback,
}: {
  role: "user" | "assistant";
  content: string;
  fallback?: React.ReactNode;
}) {
  const isUser = role === "user";

  return (
    <div className={`flex gap-2.5 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
          aria-hidden="true"
        >
          <Sparkles className="h-3.5 w-3.5" />
        </span>
      )}

      <div
        data-testid={`message-${role}`}
        className={[
          "max-w-[80%] whitespace-pre-wrap px-3.5 py-2.5 text-sm leading-relaxed",
          isUser
            ? // Squared inner corner points the bubble at its author.
              "rounded-2xl rounded-br-md bg-primary text-primary-foreground"
            : "rounded-2xl rounded-bl-md bg-muted text-foreground",
        ].join(" ")}
      >
        {content || fallback}
      </div>
    </div>
  );
}
